import {
  getPartnerBusinessType,
  type PublisherPartnerFields,
} from './partnerType';

export type OnboardingBranch = 'publisher' | 'cinema' | 'distributor';

export function getOnboardingBranch(
  publisher: PublisherPartnerFields | string | null | undefined
): OnboardingBranch {
  const businessType =
    typeof publisher === 'string' || publisher == null
      ? getPartnerBusinessType(publisher)
      : getPartnerBusinessType(publisher.business_type, publisher.partner_type);

  if (businessType === 'theater') return 'cinema';
  if (businessType === 'distributor') return 'distributor';
  return 'publisher';
}

export const PUBLISHER_CONTRACT_VERSION = 'v1.0';
export const CINEMA_CONTRACT_VERSION = 'cinema-v1.0';
export const DISTRIBUTION_CONTRACT_VERSION = 'distribution-v1.0';

export const PUBLISHER_CONTRACT_TITLE = 'Kinora Publisher Agreement';
export const CINEMA_CONTRACT_TITLE = 'Kinora Cinema Exhibition Agreement';
export const DISTRIBUTION_CONTRACT_TITLE = 'Kinora Distribution Partner Agreement';

export const CINEMA_CONTRACT_TEXT = `KINORA CINEMA EXHIBITION AGREEMENT
Version cinema-v1.0 — Effective June 2026

This Cinema Exhibition Agreement ("Agreement") is between Kinora Inc. ("Kinora") and the cinema partner completing onboarding ("Exhibitor," "you").

By accepting below, you agree to exhibit Kinora-licensed works under publisher-set terms on the Theatre Marketplace.

─────────────────────────────────────────────────────────────

1. EXHIBITOR OBLIGATIONS

1.1 Exhibitor operates one or more physical cinema locations linked to this partner account.
1.2 Exhibitor may browse the Theatre Marketplace catalog and book screening dates for listed works.
1.3 Ticket prices and theatrical windows are set by the publishing studio on each work. Exhibitor inherits those terms when booking a screening.
1.4 Exhibitor must not stream Kinora catalog titles to patrons outside a booked theatrical window unless expressly permitted in writing.

─────────────────────────────────────────────────────────────

2. REVENUE

2.1 Box office revenue from booked screenings is split per the rates shown at booking time.
2.2 Kinora may settle exhibitor shares via Stripe Connect or invoicing as configured in the partner portal.
2.3 Exhibitor is responsible for local taxes, licensing, and compliance with exhibition law in its jurisdiction.

─────────────────────────────────────────────────────────────

3. TERM

3.1 This Agreement begins on acceptance and continues until terminated by either party with 30 days written notice.
3.2 Kinora may suspend marketplace access for material breach or failed compliance review.

─────────────────────────────────────────────────────────────

Questions: partners@kinora.tv`;

export const DISTRIBUTION_CONTRACT_TEXT = `KINORA DISTRIBUTION PARTNER AGREEMENT
Version distribution-v1.0 — Effective June 2026

This Distribution Partner Agreement ("Agreement") is between Kinora Inc. ("Kinora") and the distributor completing onboarding ("Distributor," "you").

By accepting below, you agree to represent and deliver Kinora-licensed works under the terms below.

─────────────────────────────────────────────────────────────

1. SCOPE

1.1 Distributor may manage multiple publisher relationships and delivery workflows through the Kinora partner portal.
1.2 Territory, format, and exclusivity for each title are defined per work or side letter — not by this master agreement alone.
1.3 Distributor represents it holds authority to enter distribution arrangements in the territories it declares.

─────────────────────────────────────────────────────────────

2. DELIVERY & REPORTING

2.1 Distributor agrees to accurate metadata, timely delivery of approved assets, and monthly reporting where required.
2.2 Kinora may audit usage logs and revenue reports tied to distributed titles.

─────────────────────────────────────────────────────────────

3. FEES

3.1 Platform and distribution fees are disclosed in the portal before each campaign or catalog import.
3.2 Kinora may withhold settlement pending resolution of reporting discrepancies.

─────────────────────────────────────────────────────────────

4. TERM

4.1 This Agreement begins on acceptance and continues until terminated with 30 days written notice.
4.2 Surviving obligations include confidentiality, payment reconciliation, and return of confidential materials.

─────────────────────────────────────────────────────────────

Questions: partners@kinora.tv`;

export type CinemaApplicationPayload = {
  theater_name: string;
  city: string;
  state: string;
  country: string;
  address_line1: string;
  contact_email: string;
  contact_phone: string;
  screen_count: number | null;
  max_capacity: number | null;
  theater_chain: string;
};

const CINEMA_APPLICATION_KEY = 'kinora_cinema_application';

export function saveCinemaApplication(payload: CinemaApplicationPayload) {
  sessionStorage.setItem(CINEMA_APPLICATION_KEY, JSON.stringify(payload));
}

export function loadCinemaApplication(): CinemaApplicationPayload | null {
  const raw = sessionStorage.getItem(CINEMA_APPLICATION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CinemaApplicationPayload;
  } catch {
    return null;
  }
}

export function clearCinemaApplication() {
  sessionStorage.removeItem(CINEMA_APPLICATION_KEY);
}
